import React, { useEffect, useState } from 'react';
import { Row, Col, Card, Table, Badge, Spinner, ProgressBar, Form, Tab, Tabs } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { FiUsers, FiBriefcase, FiTrendingUp, FiCheckCircle, FiAlertTriangle } from 'react-icons/fi';
import adminService, { LawyerStats, ComplianceReport, User } from '../../services/admin.service';

const Reports: React.FC = () => {
  const [lawyerStats, setLawyerStats] = useState<LawyerStats[]>([]);
  const [complianceReport, setComplianceReport] = useState<ComplianceReport[]>([]);
  const [lawyers, setLawyers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedLawyer, setSelectedLawyer] = useState<string>('');
  const [casesByLawyer, setCasesByLawyer] = useState<any[]>([]);
  const [loadingCases, setLoadingCases] = useState(false);

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    try {
      const [stats, compliance, users] = await Promise.all([
        adminService.getLawyerStats(),
        adminService.getComplianceReport(),
        adminService.listUsers()
      ]);
      setLawyerStats(stats);
      setComplianceReport(compliance);
      setLawyers(users.filter(u => ['super_admin', 'coordinador', 'abogado'].includes(u.role_code || '')));
    } catch (error) {
      console.error('Error loading reports:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCasesByLawyer = async (lawyerId: string) => {
    setSelectedLawyer(lawyerId);
    if (!lawyerId) {
      setCasesByLawyer([]);
      return;
    }
    setLoadingCases(true);
    try {
      const cases = await adminService.getCasesByLawyer(Number(lawyerId));
      setCasesByLawyer(cases);
    } catch (error) {
      console.error('Error loading cases:', error);
    } finally {
      setLoadingCases(false);
    }
  };

  const getComplianceColor = (percentage: number) => {
    if (percentage >= 80) return 'success';
    if (percentage >= 50) return 'warning';
    return 'danger';
  };

  const getSemaphoreBadge = (color: string) => {
    const colorMap: Record<string, string> = {
      green: 'success',
      yellow: 'warning',
      red: 'danger'
    };
    return <Badge bg={colorMap[color] || 'secondary'}>●</Badge>;
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { bg: string; label: string }> = {
      active: { bg: 'success', label: 'Activo' },
      closed: { bg: 'secondary', label: 'Cerrado' },
      suspended: { bg: 'warning', label: 'Suspendido' }
    };
    const config = statusMap[status] || { bg: 'secondary', label: status };
    return <Badge bg={config.bg}>{config.label}</Badge>;
  };

  // Calculate totals
  const totalCases = lawyerStats.reduce((sum, l) => sum + l.total_cases, 0);
  const totalActive = lawyerStats.reduce((sum, l) => sum + l.active_cases, 0);
  const totalClosed = lawyerStats.reduce((sum, l) => sum + l.closed_cases, 0);
  const avgCompliance = lawyerStats.length > 0 
    ? Math.round(lawyerStats.reduce((sum, l) => sum + l.compliance_percentage, 0) / lawyerStats.length) 
    : 0;

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  return (
    <div className="reports-page">
      <div className="page-header mb-4">
        <h4 className="page-title">Reportes</h4>
        <nav aria-label="breadcrumb">
          <ol className="breadcrumb mb-0">
            <li className="breadcrumb-item"><Link to="/">Inicio</Link></li>
            <li className="breadcrumb-item active">Reportes</li>
          </ol>
        </nav>
      </div>

      {/* Summary Cards */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="d-flex align-items-center">
              <div className="flex-grow-1">
                <h6 className="text-muted mb-1">Total Casos</h6>
                <h3 className="mb-0">{totalCases}</h3>
              </div>
              <div className="bg-primary bg-opacity-10 rounded-circle p-3">
                <FiBriefcase className="text-primary" size={24} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="d-flex align-items-center">
              <div className="flex-grow-1">
                <h6 className="text-muted mb-1">Casos Activos</h6>
                <h3 className="mb-0">{totalActive}</h3>
              </div>
              <div className="bg-success bg-opacity-10 rounded-circle p-3">
                <FiTrendingUp className="text-success" size={24} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="d-flex align-items-center">
              <div className="flex-grow-1">
                <h6 className="text-muted mb-1">Casos Cerrados</h6>
                <h3 className="mb-0">{totalClosed}</h3>
              </div>
              <div className="bg-secondary bg-opacity-10 rounded-circle p-3">
                <FiCheckCircle className="text-secondary" size={24} />
              </div>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="border-0 shadow-sm h-100">
            <Card.Body className="d-flex align-items-center">
              <div className="flex-grow-1">
                <h6 className="text-muted mb-1">Cumplimiento Prom.</h6>
                <h3 className="mb-0">{avgCompliance}%</h3>
              </div>
              <div className={`bg-${getComplianceColor(avgCompliance)} bg-opacity-10 rounded-circle p-3`}>
                <FiAlertTriangle className={`text-${getComplianceColor(avgCompliance)}`} size={24} />
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Tabs defaultActiveKey="lawyers" className="mb-4">
        <Tab eventKey="lawyers" title="Estadísticas por Abogado">
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom">
              <div className="d-flex align-items-center">
                <FiUsers className="me-2" />
                <h5 className="mb-0">Casos por Abogado</h5>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>Abogado</th>
                    <th className="text-center">Total</th>
                    <th className="text-center">Activos</th>
                    <th className="text-center">Cerrados</th>
                    <th className="text-center">Suspendidos</th>
                    <th className="text-center">Semáforo</th>
                    <th className="text-center">% Cumplimiento</th>
                  </tr>
                </thead>
                <tbody>
                  {lawyerStats.map((lawyer) => (
                    <tr key={lawyer.lawyer_id}>
                      <td>
                        <strong>{lawyer.lawyer_name}</strong>
                      </td>
                      <td className="text-center">{lawyer.total_cases}</td>
                      <td className="text-center">
                        <Badge bg="success">{lawyer.active_cases}</Badge>
                      </td>
                      <td className="text-center">
                        <Badge bg="secondary">{lawyer.closed_cases}</Badge>
                      </td>
                      <td className="text-center">
                        <Badge bg="warning">{lawyer.suspended_cases}</Badge>
                      </td>
                      <td className="text-center">
                        <span className="me-1" title="Verde">
                          <Badge bg="success">{lawyer.green_cases}</Badge>
                        </span>
                        <span className="me-1" title="Amarillo">
                          <Badge bg="warning">{lawyer.yellow_cases}</Badge>
                        </span>
                        <span title="Rojo">
                          <Badge bg="danger">{lawyer.red_cases}</Badge>
                        </span>
                      </td>
                      <td className="text-center" style={{ minWidth: '150px' }}>
                        <div className="d-flex align-items-center">
                          <ProgressBar 
                            now={lawyer.compliance_percentage} 
                            variant={getComplianceColor(lawyer.compliance_percentage)}
                            className="flex-grow-1 me-2"
                            style={{ height: '8px' }}
                          />
                          <span className={`text-${getComplianceColor(lawyer.compliance_percentage)} fw-bold`}>
                            {lawyer.compliance_percentage}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {lawyerStats.length === 0 && (
                    <tr>
                      <td colSpan={7} className="text-center py-4 text-muted">
                        No hay datos disponibles
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="compliance" title="Cumplimiento de Tareas">
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom">
              <div className="d-flex align-items-center">
                <FiCheckCircle className="me-2" />
                <h5 className="mb-0">Reporte de Cumplimiento de Tareas</h5>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              <Table responsive hover className="mb-0">
                <thead className="bg-light">
                  <tr>
                    <th>Abogado</th>
                    <th className="text-center">Total Casos</th>
                    <th className="text-center">Tareas Completadas</th>
                    <th className="text-center">Tareas Pendientes</th>
                    <th className="text-center">Tareas Vencidas</th>
                    <th className="text-center">% Cumplimiento</th>
                  </tr>
                </thead>
                <tbody>
                  {complianceReport.map((report) => (
                    <tr key={report.lawyer_id}>
                      <td>
                        <strong>{report.lawyer_name}</strong>
                      </td>
                      <td className="text-center">{report.total_cases}</td>
                      <td className="text-center">
                        <Badge bg="success">{report.completed_tasks}</Badge>
                      </td>
                      <td className="text-center">
                        <Badge bg="warning">{report.pending_tasks}</Badge>
                      </td>
                      <td className="text-center">
                        <Badge bg="danger">{report.overdue_tasks}</Badge>
                      </td>
                      <td className="text-center" style={{ minWidth: '150px' }}>
                        <div className="d-flex align-items-center">
                          <ProgressBar 
                            now={report.task_compliance} 
                            variant={getComplianceColor(report.task_compliance)}
                            className="flex-grow-1 me-2"
                            style={{ height: '8px' }}
                          />
                          <span className={`text-${getComplianceColor(report.task_compliance)} fw-bold`}>
                            {report.task_compliance}%
                          </span>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {complianceReport.length === 0 && (
                    <tr>
                      <td colSpan={6} className="text-center py-4 text-muted">
                        No hay datos disponibles
                      </td>
                    </tr>
                  )}
                </tbody>
              </Table>
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="cases" title="Casos por Abogado">
          <Card className="border-0 shadow-sm">
            <Card.Header className="bg-white border-bottom">
              <div className="d-flex align-items-center justify-content-between">
                <div className="d-flex align-items-center">
                  <FiBriefcase className="me-2" />
                  <h5 className="mb-0">Detalle de Casos por Abogado</h5>
                </div>
                <Form.Select 
                  style={{ width: '250px' }}
                  value={selectedLawyer}
                  onChange={(e) => loadCasesByLawyer(e.target.value)}
                >
                  <option value="">Seleccionar abogado...</option>
                  {lawyers.map(lawyer => (
                    <option key={lawyer.id} value={lawyer.id}>
                      {lawyer.full_name}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </Card.Header>
            <Card.Body className="p-0">
              {loadingCases ? (
                <div className="text-center py-4">
                  <Spinner animation="border" variant="primary" size="sm" />
                </div>
              ) : (
                <Table responsive hover className="mb-0">
                  <thead className="bg-light">
                    <tr>
                      <th></th>
                      <th>Número de Caso</th>
                      <th>Cliente</th>
                      <th>Tipo de Proceso</th>
                      <th>Estado</th>
                      <th>Próxima Acción</th>
                    </tr>
                  </thead>
                  <tbody>
                    {casesByLawyer.map((caso) => (
                      <tr key={caso.id}>
                        <td>{getSemaphoreBadge(caso.semaphore_color)}</td>
                        <td>
                          <Link to={`/cases/${caso.id}`}>{caso.case_number}</Link>
                        </td>
                        <td>{caso.client_name}</td>
                        <td>{caso.process_type || '-'}</td>
                        <td>{getStatusBadge(caso.status)}</td>
                        <td>{caso.next_action_date ? new Date(caso.next_action_date).toLocaleDateString('es-CO') : '-'}</td>
                      </tr>
                    ))}
                    {!selectedLawyer && (
                      <tr>
                        <td colSpan={6} className="text-center py-4 text-muted">
                          Seleccione un abogado para ver sus casos
                        </td>
                      </tr>
                    )}
                    {selectedLawyer && casesByLawyer.length === 0 && (
                      <tr>
                        <td colSpan={6} className="text-center py-4 text-muted">
                          El abogado no tiene casos asignados
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>
        </Tab>
      </Tabs>
    </div>
  );
};

export default Reports;
